import { c as _c } from "react/compiler-runtime";
import React, { useReducer } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { LocalizedNameFields, PhoneInput, SelectInput, TextInput, TextareaInput } from '@/components/inputs';
import { addPrefixToDataParams } from '@/utils/string';
import { Button } from '@/components/buttons';
import { required, email, phone, pipeValidators } from '@/utils/validators';
import { useInitialValues } from '@/hooks/useInitialValues';
import { useCustomerRequestCreateQuery } from '@/api/skippay/customerRequestCreateQuery';
import Loader from '@/components/Loader';
import { SuccessMessage } from './SuccessMessage';
import { operations } from '@/api/skippay/generated/selfCare';
import { FieldValidator } from 'final-form';
import IconMail from '@/assets/icons/heroIcons/Mail.svg';
import { logEvent } from '@/analytics/events';
import csTranslations from '@/lang/cs/personalDataRequests.json';
const FIELD_EMAIL = 'email';
const FIELD_FIRST_NAME = 'firstName';
const FIELD_LAST_NAME = 'lastName';
const FIELD_MESSAGE = 'message';
const FIELD_PHONE = 'phoneNumber';
const FIELD_SUBJECT = 'subject';
type FormValues = {
  [FIELD_EMAIL]: string;
  [FIELD_FIRST_NAME]: string;
  [FIELD_LAST_NAME]: string;
  [FIELD_MESSAGE]: string;
  [FIELD_PHONE]: string;
  [FIELD_SUBJECT]: string;
};
type SubjectOption = {
  label: string;
  value: string;
  messageRequired: boolean;
  messagePlaceholder?: string;
};
type SubjectGroup = {
  label: string;
  category: CustomerRequestCategory;
  options: SubjectOption[];
};
enum CustomerRequestStatusEnum {
  ACCEPTED = 'ACCEPTED',
  UNCONFIRMED = 'UNCONFIRMED',
}
type CustomerRequestBody = operations['customerRequestCreate']['requestBody']['content']['application/json'];
type CustomerRequestCategory = NonNullable<CustomerRequestBody['category']>;
const csOpts = csTranslations.personalDataRequests.form.fields.interest.options;

// TODO: remove the legacy subject aliases after 2027-02-26
const LEGACY_SUBJECTS: Record<string, string> = {
  'Chci získat všechny osobní údaje, které o mně máte uložené': csOpts.rightAccess,
  'Chci podat námitku proti zpracovávání mých osobních údajů': csOpts.rightObjection,
  'Chci zaktualizovat své osobní údaje': csOpts.contactDetailsChange,
  'Chcem aktualizovať svoje osobné údaje.': csOpts.contactDetailsChange,
  'Frissíteni szeretném személyes adataimat': csOpts.contactDetailsChange,
  'Chci přenést své osobní údaje jinam': csOpts.rightPortability,
  'Chci zrušit balíček MINI a smazat osobní údaje': csOpts.rightErasure,
  'Chci odvolat udělený souhlas se zpracováním osobních údajů': csOpts.rightObjection,
  'Chci přezkoumat automatizované rozhodnutí v rámci odložení platby': csOpts.rightAutomatedReview,
  'Chci zrušit balíček MAXI a požaduji smazat osobní údaje': csOpts.rightErasure,
  'Chci zrušit balíček MAXI a požaduji smazat osobní údaje. Nevolejte mi prosím.': csOpts.rightErasure,
  'Chci podat reklamaci': csOpts.complaint,
  'Žádost o manuální překontrolování': csOpts.rightAutomatedReview
};
const LOCALIZED_SUBJECTS: Record<string, string> = {
  'Zmena kontaktných údajov': csOpts.contactDetailsChange,
  'Elérhetőségek módosítása': csOpts.contactDetailsChange
};
export const PersonalDataForm = () => {
  const $ = _c(30);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["personalDataRequests", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const router = useRouter();
  const initialValues = useInitialValues();
  const customerRequestQuery = useCustomerRequestCreateQuery();
  const querySubject = Array.isArray(router.query.s) ? router.query.s[0] : router.query.s;
  const requestedSubject = querySubject || initialValues[FIELD_SUBJECT];
  const resolvedSubject = requestedSubject ? LEGACY_SUBJECTS[requestedSubject] || LOCALIZED_SUBJECTS[requestedSubject] || requestedSubject : undefined;
  let messagePlaceholderByValue;
  let messageRequiredByValue;
  let options;
  let subjectOptions;
  let t1;
  if ($[1] !== resolvedSubject || $[2] !== t) {
    const subjectGroups = [{
      label: t("form.fields.interest.groups.contracts"),
      category: "CONTRACTS",
      options: [{
        label: t("form.fields.interest.options.contractTermination"),
        value: csOpts.contractTermination,
        messageRequired: false
      }, {
        label: t("form.fields.interest.options.contractWithdrawal14"),
        value: csOpts.contractWithdrawal14,
        messageRequired: false
      }, ...(resolvedSubject === csOpts.repaymentSchedule ? [{
        label: t("form.fields.interest.options.repaymentSchedule"),
        value: csOpts.repaymentSchedule,
        messageRequired: false
      }] : [])]
    }, {
      label: t("form.fields.interest.groups.serviceRequests"),
      category: "SERVICE_REQUESTS",
      options: [{
        label: t("form.fields.interest.options.contactDetailsChange"),
        value: csOpts.contactDetailsChange,
        messageRequired: true,
        messagePlaceholder: t("form.fields.interest.placeholder.contactDetailsChange")
      }]
    }, {
      label: t("form.fields.interest.groups.dataRights"),
      category: "PERSONAL_DATA_RIGHTS",
      options: [{
        label: t("form.fields.interest.options.rightAccess"),
        value: csOpts.rightAccess,
        messageRequired: false
      }, {
        label: t("form.fields.interest.options.rightRectification"),
        value: csOpts.rightRectification,
        messageRequired: true
      }, {
        label: t("form.fields.interest.options.rightErasure"),
        value: csOpts.rightErasure,
        messageRequired: false
      }, {
        label: t("form.fields.interest.options.rightRestriction"),
        value: csOpts.rightRestriction,
        messageRequired: true
      }, {
        label: t("form.fields.interest.options.rightObjection"),
        value: csOpts.rightObjection,
        messageRequired: true
      }, {
        label: t("form.fields.interest.options.rightPortability"),
        value: csOpts.rightPortability,
        messageRequired: true
      }, {
        label: t("form.fields.interest.options.rightAutomatedReview"),
        value: csOpts.rightAutomatedReview,
        messageRequired: false
      }]
    }, {
      label: t("form.fields.interest.groups.complaints"),
      category: "COMPLAINTS",
      options: [{
        label: t("form.fields.interest.options.complaint"),
        value: csOpts.complaint,
        messageRequired: false
      }]
    }];
    subjectOptions = subjectGroups.flatMap(_temp);
    options = subjectGroups.map(_temp3);
    messageRequiredByValue = subjectOptions.reduce(_temp4, {});
    messagePlaceholderByValue = subjectOptions.reduce(_temp5, {});
    t1 = subjectGroups.reduce(_temp6, {});
    $[1] = resolvedSubject;
    $[2] = t;
    $[3] = messagePlaceholderByValue;
    $[4] = messageRequiredByValue;
    $[5] = options;
    $[6] = subjectOptions;
    $[7] = t1;
  } else {
    messagePlaceholderByValue = $[3];
    messageRequiredByValue = $[4];
    options = $[5];
    subjectOptions = $[6];
    t1 = $[7];
  }
  const categoryByValue = t1;
  const initialSubject = subjectOptions.find(option_2 => option_2.value === resolvedSubject)?.value;
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const [t3, send] = useReducer(_temp7, t2);
  const {
    success,
    error
  } = t3;
  let t4;
  if ($[9] !== initialSubject || $[10] !== messageRequiredByValue) {
    t4 = (value_0, t5) => {
      const allValues = t5 === undefined ? {} : t5;
      return messageRequiredByValue[allValues[FIELD_SUBJECT] || initialSubject || ""] ? required(value_0) : undefined;
    };
    $[9] = initialSubject;
    $[10] = messageRequiredByValue;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const requiredMessage = t4;
  let t5;
  if ($[12] !== categoryByValue || $[13] !== customerRequestQuery) {
    t5 = async data => {
      logEvent("generate_lead", {
        action: "customerData"
      });
      ;
      try {
        await customerRequestQuery.mutate({
          ...data,
          category: categoryByValue[data.subject]
        });
        send("OK");
      } catch (t6) {
        send("KO");
      }
    };
    $[12] = categoryByValue;
    $[13] = customerRequestQuery;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== customerRequestQuery.data?.status || $[16] !== customerRequestQuery.isPending || $[17] !== error || $[18] !== initialSubject || $[19] !== initialValues || $[20] !== messagePlaceholderByValue || $[21] !== messageRequiredByValue || $[22] !== options || $[23] !== requiredMessage || $[24] !== success || $[25] !== t) {
    t6 = t7 => {
      const {
        handleSubmit,
        form
      } = t7;
      const {
        values: t8
      } = form.getState();
      const {
        subject
      } = t8 === undefined ? {} : t8;
      const activeSubject = subject || initialSubject || "";
      const messageRequired = messageRequiredByValue[activeSubject];
      const messagePlaceholder = messagePlaceholderByValue[activeSubject];
      if (customerRequestQuery.isPending) {
        return <Loader hideLabel={true} />;
      }
      if (error) {
        return <div className="text-center"><p className="mb-6 text-sm font-semibold text-red-500 lg:text-base">{t("form.error.text")}</p><Button variant="unstyled" onClick={() => send("RESET")} analyticsParam="sendNewRequest" testId="sendNewRequest" className="text-blue-500 underline decoration-1 hover:text-blue-800" align="center">{t("form.error.cta")}</Button></div>;
      }
      if (success) {
        if (customerRequestQuery.data?.status === CustomerRequestStatusEnum.ACCEPTED) {
          return <SuccessMessage isRefund={false} />;
        }
        return <div className="flex flex-col items-center justify-center gap-6 text-center"><IconMail className="mx-auto h-10 w-10 text-blue-500" /><h3 className="text-lg font-bold lg:text-4xl">{t("form.success.title")}</h3><p>{t("form.success.text")}</p><Button variant="unstyled" onClick={() => {
            form.batch(() => {
              form.change(FIELD_SUBJECT, "");
              form.change(FIELD_MESSAGE, "");
              send("RESET");
            });
          }} analyticsParam="sendNewRequest" testId="sendNewRequest" className="text-blue-500 underline decoration-1 hover:text-blue-800">{t("form.success.cta")}</Button></div>;
      }
      return <form method="post" onSubmit={data_0 => handleSubmit(data_0)} {...addPrefixToDataParams({
        analyticsParam: "personalDataForm",
        testId: "personalDataForm",
        prefix: "form"
      })}><div className="mb-10 flex flex-col gap-8"><div className="flex flex-col gap-4"><p className="font-semibold">{t("form.category.interest")}</p><div><Field name={FIELD_SUBJECT} validate={required} initialValue={initialSubject}>{fieldRenderProps => <SelectInput hideLabel={true} {...fieldRenderProps} label={t("form.fields.interest.title")} options={options} />}</Field></div></div><div className="flex flex-col gap-4"><p className="font-semibold">{t("form.category.contact")}</p><LocalizedNameFields nameField={{
              name: FIELD_FIRST_NAME,
              label: t("core:forms.fields.name"),
              validate: required,
              initialValue: initialValues[FIELD_FIRST_NAME]
            }} surnameField={{
              name: FIELD_LAST_NAME,
              label: t("core:forms.fields.surname"),
              validate: required,
              initialValue: initialValues[FIELD_LAST_NAME]
            }} /><Field name={FIELD_EMAIL} type="email" validate={pipeValidators(required, email)} initialValue={initialValues[FIELD_EMAIL]}>{fieldRenderProps_0 => <TextInput {...fieldRenderProps_0} label={t("core:forms.fields.email")} />}</Field>{activeSubject === csOpts.repaymentSchedule && <Field name={FIELD_PHONE} type="tel" validate={pipeValidators(required, phone)} initialValue={initialValues[FIELD_PHONE]}>{fieldRenderProps_1 => <PhoneInput {...fieldRenderProps_1} label={t("core:forms.fields.phone")} />}</Field>}</div><div className="flex flex-col gap-4"><p className="font-semibold">{t(messageRequired ? "form.category.messageRequired" : "form.category.message")}</p><Field name={FIELD_MESSAGE} initialValue={initialValues[FIELD_MESSAGE]} validate={requiredMessage}>{fieldRenderProps_2 => <TextareaInput {...fieldRenderProps_2} label={messagePlaceholder || (messageRequired ? t("form.fields.message.labelRequired") : t("form.fields.message.label"))} hideLabel={true} />}</Field></div></div><Button type="submit" analyticsParam="submitForm" testId="submitForm" variant="primary" align="center" size="lg" busy={customerRequestQuery.isPending}>{t("core:forms.fields.submit")}</Button></form>;
    };
    $[15] = customerRequestQuery.data?.status;
    $[16] = customerRequestQuery.isPending;
    $[17] = error;
    $[18] = initialSubject;
    $[19] = initialValues;
    $[20] = messagePlaceholderByValue;
    $[21] = messageRequiredByValue;
    $[22] = options;
    $[23] = requiredMessage;
    $[24] = success;
    $[25] = t;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== t5 || $[28] !== t6) {
    t7 = <div className="rounded-xl bg-white px-4 py-6 lg:rounded-4xl lg:p-12"><Form onSubmit={t5}>{t6}</Form></div>;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  return t7;
};
function _temp(group) {
  return group.options;
}
function _temp2(t0) {
  const {
    label,
    value
  } = t0;
  return {
    label,
    value
  };
}
function _temp3(group_0) {
  return {
    label: group_0.label,
    options: group_0.options.map(_temp2)
  };
}
function _temp4(acc, option) {
  acc[option.value] = option.messageRequired;
  return acc;
}
function _temp5(acc_0, option_0) {
  if (option_0.messagePlaceholder) {
    acc_0[option_0.value] = option_0.messagePlaceholder;
  }
  return acc_0;
}
function _temp6(acc_1, group_1) {
  group_1.options.forEach(option_1 => {
    acc_1[option_1.value] = group_1.category;
  });
  return acc_1;
}
function _temp7(_, action) {
  switch (action) {
    default:
      {
        throw new Error(`Unknown action ${action}`);
      }
    case "OK":
      {
        return {
          success: true
        };
      }
    case "KO":
      {
        return {
          error: true
        };
      }
    case "RESET":
      {
        return {};
      }
  }
}